/**
 * Presentation logic for the Engagements tab (f7.1).
 *
 * Everything here is pure, so the rules that shape the table and its two
 * toolbar selects are testable without rendering anything. Nothing in this
 * file decides what a caller may see or do: visibility is the server's answer,
 * and so are the status label, the stage label and the Project Head.
 *
 * **The search and both selects run client-side**, over the set the server
 * returned whole. Engagements per client run **1 to 7** in the legacy -- 26 of
 * the 39 clients have exactly one -- and the collection takes no `q`, `stage`,
 * `status`, `page`, `sort` or `order` parameter, which keeps f4.1's *"no
 * operation accepts a pagination, search, filter or sort parameter"* assertion
 * true verbatim for a **fourth** feature.
 *
 * **There is no pagination here at all**, and that is a departure from f6.1
 * one level up: the prototype draws page controls on the Clients screen
 * because 39 rows across four pages is a real experience, and draws none on
 * this tab because seven rows is not. The revisit condition is recorded rather
 * than left implicit: when a client's engagements outgrow a screenful, the tab
 * grows controls -- and that is a decision with a real number behind it.
 */

import type {
  CurrentOwner,
  Engagement,
  EngagementElement,
} from '@/api/client';
import { ownerFor } from '@/routes/business-unit-groups/ownershipView';
import { sortRows, type SortState, type SortValue } from '@/ui/tableSort';

/** The status code the screen styles as "good". Anything else is neutral. */
const ACTIVE = 'ACTIVE';

/**
 * The delivery role an Engagement supports, and the only one.
 *
 * Named here only to LABEL and ORDER the Project Head column and the assign
 * dialog's selector. The server refuses anything else with `409
 * DELIVERY_ROLE_LEVEL_MISMATCH`, and it is the node-type registry -- not this
 * array -- that decides what the dialog offers.
 */
export const ENGAGEMENT_ROLE_CODES = ['PROJECT_HEAD'] as const;

/** The role the Project Head column renders. */
export const PROJECT_HEAD = 'PROJECT_HEAD';

/**
 * The badge class for a status.
 *
 * Only the CLASS is derived from the code -- the LABEL always comes from the
 * server's `status.name`, so a status renamed in the lookup table renames
 * itself here with no frontend change (f1.3's rule, restated by f2.2).
 */
export function badgeClassFor(statusCode: string): string {
  return statusCode === ACTIVE ? 'badge pill-success' : 'badge pill-neutral';
}

/**
 * The badge class for a delivery stage.
 *
 * Keyed on the CODE, never on the name, and the name is still what renders --
 * so an Admin renaming `Build & Deliver` on the Settings screen renames it
 * here too. A stage this map does not know renders neutral rather than
 * unstyled, which is what makes a fifth stage inserted by an Admin work with
 * no frontend change: exactly the property custom requirement 8 asks for.
 */
const STAGE_CLASSES: Readonly<Record<string, string>> = {
  CONTRACT_SIGNED: 'badge pill-info',
  BUILD_DELIVER: 'badge pill-warning',
  DELIVERY_COMPLETE: 'badge pill-neutral',
  FINAL_CLOSE: 'badge pill-neutral',
};

export function stageClassFor(stageCode: string | undefined): string {
  return (stageCode && STAGE_CLASSES[stageCode]) || 'badge pill-neutral';
}

export function isActive(engagement: Engagement): boolean {
  return engagement.status.code === ACTIVE;
}

/** The engagement's own current Project Head, or undefined. */
export function projectHeadFor(
  engagement: Engagement,
): CurrentOwner | undefined {
  return ownerFor(engagement, PROJECT_HEAD);
}

/**
 * Which column the table is sorted by.
 *
 * Every column the table renders is here except the one holding the row
 * actions and the expand toggle, which have nothing to order by.
 */
export type SortColumn =
  | 'code'
  | 'name'
  | 'projectHead'
  | 'productType'
  | 'stage'
  | 'start'
  | 'end'
  | 'elements'
  | 'status';

/**
 * The default order: by name, as the server already sent it.
 *
 * `name` rather than `code`, following the two levels above: the name is what
 * the second column shows and what a reader recognises. The KE code is issued
 * by Kimble and sorts by issue order, which is not an order anybody means.
 */
export const DEFAULT_SORT: SortState<SortColumn> = {
  column: 'name',
  direction: 'asc',
};

/** What a column sorts by for one engagement. */
export function sortValueOf(
  engagement: Engagement,
  column: SortColumn,
): SortValue {
  switch (column) {
    case 'code':
      return engagement.code;
    case 'projectHead':
      return projectHeadFor(engagement)?.display_name ?? null;
    case 'productType':
      return engagement.product_type ?? null;
    case 'stage':
      return engagement.delivery_stage?.name ?? null;
    case 'start':
      // The ISO string sorts correctly as text AND as a date, so no parse is
      // needed -- and a null start is impossible, because both dates are
      // required (70 of 70 rows populate them).
      return engagement.start_date;
    case 'end':
      return engagement.end_date;
    case 'elements':
      return engagement.element_count;
    case 'status':
      return engagement.status.name;
    default:
      return engagement.name;
  }
}

/** The two toolbar selects. An empty string means "all". */
export interface EngagementFilters {
  stageCode: string;
  statusCode: string;
}

export const NO_FILTERS: EngagementFilters = { stageCode: '', statusCode: '' };

/** Whether either select is narrowing the list. */
export function isFiltered(filters: EngagementFilters): boolean {
  return Object.values(filters).some((value) => value !== '');
}

export interface TableView {
  search: string;
  filters: EngagementFilters;
  sort: SortState<SortColumn>;
}

/**
 * Search across the KE code and the name, case-insensitively.
 *
 * The prototype's placeholder promises exactly these two -- *"Search
 * engagement or KE code"* -- and there is nothing else worth adding: an
 * engagement has no alias table, because unlike a client it is identified by a
 * code every feed carries.
 */
export function applySearch(
  engagements: readonly Engagement[],
  search: string,
): Engagement[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return [...engagements];
  return engagements.filter(
    (engagement) =>
      engagement.code.toLowerCase().includes(needle) ||
      engagement.name.toLowerCase().includes(needle),
  );
}

/** The prototype's two selects, client-side over the loaded set. */
export function applyFilters(
  engagements: readonly Engagement[],
  filters: EngagementFilters,
): Engagement[] {
  return engagements.filter((engagement) => {
    if (
      filters.stageCode &&
      engagement.delivery_stage?.code !== filters.stageCode
    ) {
      return false;
    }
    if (filters.statusCode && engagement.status.code !== filters.statusCode) {
      return false;
    }
    return true;
  });
}

export function applySort(
  engagements: readonly Engagement[],
  sort: SortState<SortColumn>,
): Engagement[] {
  return sortRows(engagements, sort, sortValueOf);
}

/** Everything that survives the search, the filters and the sort. */
export function visibleEngagements(
  engagements: readonly Engagement[],
  view: TableView,
): Engagement[] {
  return applySort(
    applyFilters(applySearch(engagements, view.search), view.filters),
    view.sort,
  );
}

export interface CodeOption {
  code: string;
  name: string;
}

/**
 * The distinct delivery stages present in the loaded set, for the select.
 *
 * Read from the ROWS rather than from the lookup catalogue, for the reason
 * f4.2 gave about its owner filters and f5.1 and f6.1 repeated: a filter
 * offering a value that matches no row is a worse control than one offering
 * the values that do. The create dialog is the opposite case and reads the
 * catalogue, because there the question is what an Admin may choose.
 */
export function stageOptions(
  engagements: readonly Engagement[],
): CodeOption[] {
  return distinctByCode(
    engagements
      .map((engagement) => engagement.delivery_stage)
      .filter((stage): stage is NonNullable<typeof stage> => Boolean(stage)),
  );
}

/** The distinct statuses present, so the select never offers an empty filter. */
export function statusOptions(
  engagements: readonly Engagement[],
): CodeOption[] {
  return distinctByCode(engagements.map((engagement) => engagement.status));
}

function distinctByCode(
  labels: readonly { code: string; name: string }[],
): CodeOption[] {
  const byCode = new Map<string, string>();
  for (const label of labels) byCode.set(label.code, label.name);
  return [...byCode.entries()]
    .map(([code, name]) => ({ code, name }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

/**
 * How many ACTIVE engagements a client has, for the tab's count badge.
 *
 * Derived from the loaded rows rather than from the client's own
 * `engagement_count`, so the badge and the table below it can never disagree
 * while a mutation is in flight. The two agree once both queries settle,
 * because the server computes the same thing.
 */
export function activeCount(engagements: readonly Engagement[]): number {
  return engagements.filter(isActive).length;
}

/**
 * A date as the table renders it: `01 Jan 2026`, the prototype's own format.
 *
 * Parsed as a plain calendar date and formatted in UTC, deliberately. The
 * server sends `YYYY-MM-DD` because every source cell has a zero time
 * component, and letting the browser apply a local offset would render
 * `01 Jan 2026` as `31 Dec 2025` for any reader west of Greenwich.
 */
export function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Whether an element has been accepted into the hierarchy. */
export function isConfirmed(element: EngagementElement): boolean {
  return Boolean(element.confirmed_at);
}

export function elementIsActive(element: EngagementElement): boolean {
  return element.status.code === ACTIVE;
}

/**
 * Which verbs an element offers, decided in one place.
 *
 * `confirm` until it is confirmed, and never again -- confirmation is not
 * reversible, because an element confirmed in error is retired by
 * deactivation. `deactivate` only once it IS confirmed: retiring something
 * never accepted is the confirmation decision in disguise, and the server
 * refuses it with `ELEMENT_NOT_CONFIRMED`. The client hides the control for
 * the same reason it hides any other -- to explain the refusal before it
 * happens, never to be the thing that enforces it.
 */
export interface ElementActions {
  confirm: boolean;
  deactivate: boolean;
  reactivate: boolean;
}

export function actionsFor(element: EngagementElement): ElementActions {
  const confirmed = isConfirmed(element);
  const active = elementIsActive(element);
  return {
    confirm: !confirmed,
    deactivate: confirmed && active,
    reactivate: !active,
  };
}

/**
 * Re-exported, not redefined: reading f2.1's error envelope is the whole API's
 * concern, and the provenance formatters are shared with the levels above.
 */
export {
  PROVENANCE_MISSING,
  formatActor,
  formatMoment,
  initialsOf,
} from '@/routes/business-unit-groups/groupView';
export { activeChildrenFrom, isRefusal } from '@/routes/clients/clientView';
export { generalMessage, problemFor } from '@/api/fieldErrors';
